import {
  type CustomType,
  documentKeys,
  getDocumentList,
} from "@prismicio/editor-fields"
import { range } from "@prismicio/editor-support/Array"
import { caseInsensitiveIncludes } from "@prismicio/editor-support/String"
import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  ConjoinedIcons,
  DefaultErrorMessage,
  Dialog,
  DialogContent,
  DialogDescription,
  dialogErrorImage,
  DialogTitle,
  ErrorBoundary,
  FormSearchInput,
  ScrollArea,
  Skeleton,
  Text,
  Tooltip,
} from "@prismicio/editor-ui"
import { useSuspenseQuery } from "@tanstack/react-query"
import { Suspense, useState } from "react"

import { env } from "../../app/env"
import { baseUrl as coreApiBaseUrl } from "../../app/services/core"
import { useCustomTypes } from "../../app/services/customTypes"
import { useRole } from "../../app/services/user"
import { useGoToNewDocument } from "../../document/documentRouting"
import { useDocumentListLocales } from "../useDocumentListLocales"
import {
  useDocumentListSearchParams,
  useRepositoryLanguages,
} from "../useDocumentListSearchParams"
import { useHasCustomTypes } from "./useHasCustomTypes"

export interface CreateNewDocumentProps {
  setModalOpen: (isOpen: boolean) => void
  size?: "small" | "large"
  color?: "grey" | "purple"
  loading?: boolean
}

export function CreateNewDocumentButton(props: CreateNewDocumentProps) {
  const { setModalOpen, color, size, loading = false } = props

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <Button startIcon="add" color={color} size={size} disabled>
            Create a new page
          </Button>
        }
      >
        <CreateNewDocument
          setModalOpen={setModalOpen}
          color={color}
          size={size}
          loading={loading}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

function CreateNewDocument(props: CreateNewDocumentProps) {
  const { setModalOpen, color, size, loading = false } = props

  const { locale } = useDocumentListLocales()
  const { isContributor } = useRole(locale)
  const userCanCreate = !isContributor

  // the user won't be able to create a new document until they have pushed custom types
  // thus we disable the button until we have some types
  const hasNoCustomTypes = !useHasCustomTypes()

  // This disables button until singleton custom types are resolved
  useExistingSingletonCustomTypes()

  return (
    <Tooltip
      visible={!userCanCreate}
      title="You can't create documents"
      content="Only users with the role of publisher or administrator can create documents."
    >
      <Button
        startIcon="add"
        color={color}
        size={size}
        disabled={hasNoCustomTypes || !userCanCreate}
        onClick={() => setModalOpen(true)}
        loading={loading}
      >
        Create a new page
      </Button>
    </Tooltip>
  )
}

interface CreateNewDocumentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  releaseId?: string
}

export function CreateNewDocumentModal(props: CreateNewDocumentModalProps) {
  const { open, onOpenChange, releaseId } = props
  const [searchTerm, setSearchTerm] = useState("")

  return (
    <Dialog
      size="small"
      hasClose={false}
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogTitle hidden>Create a new page</DialogTitle>
      <DialogDescription hidden>
        Choose a page type to create a new page.
      </DialogDescription>
      <DialogContent>
        <Box
          flexDirection="column"
          maxHeight={460}
          height="100%"
          gap={4}
          padding={16}
        >
          <Box flexDirection="column" padding={{ bottom: 8 }}>
            <FormSearchInput value={searchTerm} onValueChange={setSearchTerm} />
          </Box>
          <ErrorBoundary renderError={() => <CustomTypesListError />}>
            <Text variant="smallBold" color="grey11">
              Page types
            </Text>
            <Suspense
              fallback={
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  gap={8}
                >
                  {range(1, 6).map((num) => (
                    <Skeleton key={num} height={56} width="100%" />
                  ))}
                </Box>
              }
            >
              <CustomTypeOptions
                searchTerm={searchTerm}
                releaseId={releaseId}
              />
            </Suspense>
          </ErrorBoundary>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

interface CustomTypeOptionsProps {
  searchTerm: string
  releaseId?: string
}
function CustomTypeOptions(props: CustomTypeOptionsProps) {
  const { searchTerm, releaseId } = props
  const customTypes = useCustomTypes().data

  const filteredCustomTypes = customTypes.filter(
    (customType) =>
      caseInsensitiveIncludes(customType.label, searchTerm) &&
      customType.status,
  )

  const orderedCustomTypes = filteredCustomTypes.sort((a, b) =>
    a.label.localeCompare(b.label),
  )

  const existingSingletonCustomTypes = useExistingSingletonCustomTypes()

  // sort custom types with existing document to the end
  const finalCustomTypes = orderedCustomTypes.sort((a, b) => {
    const conditionA = existingSingletonCustomTypes.includes(a.id)
    const conditionB = existingSingletonCustomTypes.includes(b.id)
    if (conditionA === conditionB) return 0
    if (conditionA) return 1
    return -1
  })

  return (
    <ScrollArea>
      <ActionList variant="borderless" gap={0}>
        {finalCustomTypes.map((customType) => {
          return (
            <CustomTypeCard
              key={customType.id}
              releaseId={releaseId}
              customType={customType}
              isExistingSingletonCustomType={existingSingletonCustomTypes.includes(
                customType.id,
              )}
            />
          )
        })}
      </ActionList>
    </ScrollArea>
  )
}

function CustomTypesListError() {
  return (
    <Box
      height="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while your custom types."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

interface CustomTypeCard {
  customType: CustomType
  releaseId?: string
  isExistingSingletonCustomType: boolean
}

function CustomTypeCard(props: CustomTypeCard) {
  const { customType, releaseId, isExistingSingletonCustomType } = props

  const { locale } = useDocumentListLocales()
  const goToNewDocument = useGoToNewDocument()

  const disabled = isExistingSingletonCustomType

  return (
    <ActionListItem
      renderStartIcon={() => (
        <ConjoinedIcons largeIconName="database" smallIconName="add" />
      )}
      description={customType.repeatable ? "Repeatable" : "Singleton"}
      onClick={() =>
        !disabled &&
        goToNewDocument({
          releaseId,
          customTypeId: customType.id,
          locale,
        })
      }
      disabled={disabled}
      endAdornment={
        isExistingSingletonCustomType && (
          <Tooltip
            visible={isExistingSingletonCustomType}
            content="Singleton page already exists in current locale"
            align="end"
          >
            <Text color="grey11" variant="small">
              Already exists
            </Text>
          </Tooltip>
        )
      }
    >
      {customType.label}
    </ActionListItem>
  )
}

/**
 * @returns list of singleton custom type ids, for which a document already exists
 */
function useExistingSingletonCustomTypes() {
  const { searchParams } = useDocumentListSearchParams()
  const { masterLocaleId } = useRepositoryLanguages()

  const customTypes = useCustomTypes().data

  const singletonCustomTypeIds = customTypes
    .filter((customType) => !customType.repeatable)
    .map((customType) => customType.id)

  const {
    data: { results: singletonDocuments },
  } = useSuspenseQuery({
    queryKey: documentKeys.list({
      baseUrl: coreApiBaseUrl,
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
      limit: singletonCustomTypeIds.length,
      language: searchParams.language?.[0] || masterLocaleId,
      customTypes: singletonCustomTypeIds,
    }),
    queryFn: ({ queryKey: [, , params] }) => getDocumentList(params),
  })

  return (
    // Take only those singleton custom type, for which we found document in the list
    singletonCustomTypeIds.filter((customTypeId) =>
      singletonDocuments.some(
        (document) => document.custom_type_id === customTypeId,
      ),
    )
  )
}
